import type { Pathway } from "../types";
import { links } from "../../linksRegistry";


export const sinusitisPathway: Pathway = {
  id: "sinusitis",
  name: "Acute sinusitis",
  population: "Adults and children aged 12 years and over",
  pgdRefs: [
    "pgd-6a-fluticasone-sinusitis",
    "pgd-6b-mometasone-sinusitis",
    "pgd-6c-penv-sinusitis",
    "pgd-6d-clari-sinusitis",
    "pgd-6e-doxy-sinusitis",
    "pgd-6f-eryth-sinusitis",
  ],
  links,
  clinicalNotes: [
    "Acute sinusitis commonly follows an upper respiratory tract infection and most cases resolve without antibiotics.",
    "Use the grouped diagnostic criteria to confirm characteristic symptoms before proceeding to treatment.",
    "Intranasal corticosteroid is the recommended first-line supply when suitable; antibiotics are reserved for patients where the pathway indicates antibiotic treatment.",
  ],
  clinicalNoteLinkRefs: ["nice-cks-sinusitis", "nhs-england-pharmacy-first-clinical-pathways", "pgd-sinusitis-fluticasone", "pgd-sinusitis-mometasone", "pgd-sinusitis-phenoxymethylpenicillin", "pgd-sinusitis-clarithromycin", "pgd-sinusitis-doxycycline", "pgd-sinusitis-erythromycin"],
  demographicConfiguration: {
    bannerFields: [
      { field: "dateOfBirth", label: "Date of birth", type: "date" },
      { field: "ageYears", label: "Age (years)", type: "number" },
      {
        field: "knownAllergies",
        label: "Known allergies",
        type: "string",
        helpText: "Penicillin allergy is confirmed again within the consultation flow",
      },
    ],
  },
  exclusionPrefillMap: {},
  entryNode: "q01_emergency",

  nodes: [

    // ═══════════════════════════════════════════════════════════════════════
    // Q01 — Emergency and red flag screen
    //
    // Purpose : Immediately identify presentations requiring urgent escalation
    //           before any clinical assessment begins.
    //
    // Includes: Standard sinusitis red flags from all PGDs + possible cancer
    //           (unilateral polyp, bloody discharge, persistent unilateral
    //           symptoms) which warrants the same urgent referral response.
    //
    // Source  : All sinusitis PGDs — red flag exclusion criteria; NICE CKS
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q01_emergency",
      type: "checklist",
      text: "Are any of the following red flags present?",
      hint: "Tick any that apply. Any selection requires urgent escalation and will end this consultation.",
      showSepsisWarning: true,
      items: [
        {
          id: "rf_sepsis",
          text: "Signs of sepsis or severe / life-threatening infection",
        },
        {
          id: "rf_orbital",
          text: "Periorbital oedema or erythema, displaced globe, double vision, reduced eye movements, or reduced visual acuity",
        },
        {
          id: "rf_intracranial",
          text: "Severe frontal headache, frontal swelling, meningism, focal neurological signs, altered consciousness, or personality change",
        },
        {
          id: "rf_very_unwell",
          text: "Systemically very unwell or rapidly deteriorating",
        },
        {
          id: "rf_cancer",
          text: "Possible cancer — unilateral polyp or mass, bloody nasal discharge, or persistent unilateral nasal obstruction / discharge / nosebleeds / crusting / facial swelling",
        },
      ],
      includeNoneOption: true,
      anySelected: "GATEWAY_EMERGENCY",
      noneSelected: "q02_shared_exclusions",
    },

    {
      // Displayed when any q01 red flag is ticked.
      // triggersPayment = true: the clinical encounter has taken place.
      id: "GATEWAY_EMERGENCY",
      type: "gateway_alarm",
      heading: "Urgent escalation required",
      body: "One or more red flags are present. Refer urgently to A&E or call 999. Consider NEWS2 to support escalation decision.",
      action: "close",
      triggersPayment: true,
      linkedCalculatorId: "news2",
      linkedCalculatorLabel: "Calculate NEWS2 score (optional)",
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q02 — Shared PGD exclusions
    //
    // Purpose : Screen for conditions that exclude supply under all sinusitis
    //           antibiotic PGDs (6c, 6d, 6e, 6f) and most steroid PGDs (6a, 6b).
    //           Asking these once, upfront, avoids repetition in later nodes
    //           and stops the consultation early where no treatment can be
    //           supplied regardless of drug choice or allergy status.
    //
    //           Drug-specific exclusions (e.g. clari QT, doxy SLE) are
    //           handled later at q12 / q13a / q13b after allergy status
    //           is established.
    //
    // Sources : PGD 6c (penv), 6d (clari), 6e (doxy), 6f (eryth) —
    //           exclusion criteria sections; cross-referenced for items
    //           present in ALL four antibiotic PGDs.
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q02_shared_exclusions",
      type: "checklist",
      text: "Do any of the following apply to the patient?",
      hint: "These conditions prevent supply under all sinusitis PGDs. Any selection will end the consultation.",
      items: [
        {
          id: "ex_unable_swallow",
          text: "Unable to absorb oral medications, or unable to swallow oral dosage formulations (tablets, capsules, or liquid)",
        },
        {
          id: "ex_failed_abx",
          text: "Failed a previous antibiotic course for this episode of sinusitis",
        },
        {
          id: "ex_nasal_trauma",
          text: "Nasal trauma, or nasal surgery where full healing has not yet occurred",
        },
        {
          id: "ex_epistaxis",
          text: "Current or recent epistaxis (nosebleed)",
        },
        {
          id: "ex_foreign_body",
          text: "Foreign body present in the nasal passage",
        },
        {
          id: "ex_recurrent",
          text: "Recurrent sinusitis — 4 or more annual episodes without persistent symptoms between episodes",
        },
        {
          id: "ex_chronic",
          text: "Chronic sinusitis — symptoms lasting more than 12 weeks",
        },
        {
          id: "ex_anatomic_defect",
          text: "Anatomical defect causing nasal obstruction",
        },
        {
          id: "ex_allergic_cause",
          text: "Suspected allergic or immunological cause of sinusitis",
        },
        {
          id: "ex_nasal_polyps",
          text: "Co-morbidities complicating management, such as nasal polyps",
        },
        {
          id: "ex_ketogenic",
          text: "Currently following a ketogenic diet",
        },
      ],
      includeNoneOption: true,
      anySelected: { outcomeId: "OUT_CANNOT_SUPPLY" },
      noneSelected: "q03_diagnostic_criteria",
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q03 — Diagnostic criteria
    //
    // Purpose : Confirm the presentation is consistent with acute sinusitis
    //           using NICE-aligned grouped diagnostic criteria.
    //
    // Rule    : At least ONE nasal symptom (Group 1) AND at least ONE
    //           associated symptom (Group 2) must be present.
    //
    // Age logic : Loss of smell is a recognised adult (≥16) diagnostic
    //             feature. In children (12–15) daytime or night-time cough
    //             is used instead, per NHS England Pharmacy First pathway.
    //
    // Source  : NHS England Pharmacy First Clinical Pathway v1.6; NICE CKS
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q03_diagnostic_criteria",
      type: "multi_select",
      text: "Confirm diagnostic criteria",
      hint: "At least one symptom from each group must be present to confirm acute sinusitis.",
      groups: [
        {
          id: "nasal",
          label: "Group 1 — Nasal symptoms (at least one required)",
          isKeyDiagnostic: true,
          items: [
            { id: "nasal_blockage", text: "Nasal blockage or congestion" },
            { id: "nasal_discharge", text: "Nasal discharge or post-nasal drip" },
          ],
        },
        {
          id: "associated",
          label: "Group 2 — Associated symptoms (at least one required)",
          isKeyDiagnostic: true,
          items: [
            { id: "facial_pain", text: "Facial pain or pressure" },
            { id: "loss_smell", text: "Reduction or loss of sense of smell", showIfAgeAtLeast: 16 },
            { id: "cough_day_night", text: "Cough during the day or at night", showIfAgeUnder: 16 },
          ],
        },
      ],
      storesTo: "diagnosticCriteria",
      routing: (selections, patient) => {
        const hasNasal = ["nasal_blockage", "nasal_discharge"].some(
          (id) => selections.includes(id),
        );
        const ageYears = patient?.ageYears ?? null;
        const isAdult = ageYears == null || ageYears >= 16;
        const associatedIds = isAdult
          ? ["facial_pain", "loss_smell"]
          : ["facial_pain", "cough_day_night"];
        const hasAssociated = associatedIds.some((id) => selections.includes(id));
        return hasNasal && hasAssociated
          ? "GATEWAY_DIAGNOSIS"
          : { outcomeId: "OUT_SELF_CARE_DIAGNOSIS_UNLIKELY" };
      },
    },

    {
      // Displayed when q03 diagnostic criteria are met.
      // triggersPayment = true — clinical gateway has been passed.
      id: "GATEWAY_DIAGNOSIS",
      type: "gateway_progression",
      heading: "Acute sinusitis — diagnostic criteria met",
      body: "Clinical features are consistent with acute sinusitis. Proceeding to eligibility and treatment assessment.",
      triggersPayment: true,
      next: "q04_age",
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q04 — Age eligibility
    //
    // Purpose : Confirm the patient is within the pathway age range (≥12).
    //           Auto-answered from demographic banner when DOB / age is known.
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q04_age",
      type: "yes_no",
      text: "Is the patient aged 12 years or over?",
      hint: "Inferred from the patient's date of birth where available.",
      required: true,
      autoAnswer: { when: { patientAgeAtLeast: 12 }, value: true },
      yes: "q05_pregnant_under16",
      no: { outcomeId: "OUT_CANNOT_UNDER12" },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q05 — Pregnant and under 16
    //
    // Purpose : No sinusitis PGD covers pregnancy in patients under 16.
    //           Auto-answered to false for patients aged ≥16.
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q05_pregnant_under16",
      type: "yes_no",
      text: "Is the patient pregnant and under 16 years of age?",
      required: true,
      autoAnswer: { when: { patientAgeAtLeast: 16 }, value: false },
      yes: { outcomeId: "OUT_CANNOT_PREGNANT_UNDER16" },
      no: "q06_symptom_duration",
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q06 — Symptom duration
    //
    // Purpose : Supply under this pathway is only appropriate when symptoms
    //           persist beyond 10 days with no improvement. Symptoms present
    //           for 10 days or fewer are self-limiting and should be managed
    //           with self-care.
    //
    // Source  : NHS England Pharmacy First Clinical Pathway v1.6
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q06_symptom_duration",
      type: "single_select",
      text: "How long has the patient had symptoms?",
      hint: "Acute sinusitis is usually self-limiting within 10 days. Supply is only appropriate when symptoms persist beyond 10 days with no improvement.",
      options: [
        {
          id: "under_10_days",
          label: "10 days or fewer — symptoms likely self-limiting",
        },
        {
          id: "over_10_days",
          label: "More than 10 days with no improvement",
        },
      ],
      routing: {
        under_10_days: { outcomeId: "OUT_SELF_CARE_DURATION" },
        over_10_days: "q07_bacterial_features",
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q07 — Bacterial sinusitis features
    //
    // Purpose : Two or more of the following features must be present before
    //           antibiotic treatment is considered. Fewer than 2 routes to
    //           the steroid-only or self-care branch (q07b).
    //
    // Source  : NHS England Pharmacy First Clinical Pathway v1.6 gateway
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q07_bacterial_features",
      type: "multi_select",
      text: "Does the patient have 2 or more of the following features suggesting acute bacterial sinusitis?",
      hint: "At least 2 must be present to proceed to antibiotic treatment assessment.",
      items: [
        { id: "bf_deterioration", text: "Marked deterioration after an initial milder phase" },
        { id: "bf_fever", text: "Fever (temperature above 38°C)" },
        { id: "bf_purulent", text: "Unremitting purulent nasal discharge" },
        { id: "bf_unilateral", text: "Severe localised unilateral pain, particularly over the teeth (toothache) and jaw" },
      ],
      minRequired: 2,
      thresholdMet: "q08_steroid_suitable",  // ≥2 features — offer steroid then antibiotic
      thresholdNotMet: "q07b_no_bacterial",      // <2 features — steroid or self-care only
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q07b — Fewer than 2 bacterial features
    //
    // Purpose : Antibiotics are not indicated when fewer than 2 bacterial
    //           features are present. Offer intranasal corticosteroid for
    //           symptom relief, or advise self-care if declined.
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q07b_no_bacterial",
      type: "yes_no",
      text: "Fewer than 2 bacterial features are present — antibiotics are not indicated. Would the patient like to try an intranasal corticosteroid?",
      hint: "High-dose intranasal corticosteroid (fluticasone or mometasone) can help relieve symptoms. Give self-care advice: sinusitis is likely viral and should resolve without antibiotics.",
      required: true,
      storesTo: "steroidOfferedNoBacterial",
      yes: "steroid_drug_select",
      no: { outcomeId: "OUT_SELF_CARE_NO_BACTERIAL" },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q08 — Intranasal corticosteroid as first-line treatment
    //
    // Purpose : When 2+ bacterial features are present, the pathway recommends
    //           offering intranasal corticosteroid before proceeding to
    //           antibiotics. Skip to antibiotic arm if unsuitable, declined,
    //           or already tried this episode.
    //
    // Source  : NHS England Pharmacy First Clinical Pathway v1.6
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q08_steroid_suitable",
      type: "yes_no",
      text: "Is intranasal corticosteroid suitable and accepted as first-line treatment?",
      hint: "Choose No if unsuitable, declined, or already tried for this episode and antibiotic treatment is now being considered.",
      required: true,
      storesTo: "steroidSuitable",
      yes: "steroid_drug_select",
      no: "q09_penicillin_allergy",
    },

    // ─── Steroid branch ──────────────────────────────────────────────────────
    // Shared entry point from q07b (no bacterial features) and q08 (steroid
    // first-line). Fluticasone furoate recommended; mometasone as override.
    // If a steroid PGD exclusion fires, fall through to the antibiotic arm.
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: "steroid_drug_select",
      type: "drug_selection",
      variant: "recommend_with_override",
      overridePgdRefs: [
        "pgd-6a-fluticasone-sinusitis",
        "pgd-6b-mometasone-sinusitis",
      ],
      rules: [
        {
          when: {},
          pgdId: "pgd-6a-fluticasone-sinusitis",
          recommendationText:
            "Fluticasone furoate nasal spray is recommended first-line. Mometasone is available as a clinically justified override.",
        },
      ],
      overrideReasons: ["Stock availability", "Patient preference", "Previous intolerance", "Clinical judgement"],
      next: "steroid_pgd_exclusions",
    },
    {
      // Formal PGD exclusion check for whichever steroid was selected.
      // If exclusion fires → fall through to antibiotic arm at q09.
      // If all clear → supply steroid.
      id: "steroid_pgd_exclusions",
      type: "pgd_exclusion_check",
      anyTriggered: "q09_penicillin_allergy",
      allCleared: { outcomeId: "OUT_SUPPLY_STEROID" },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q09 — Penicillin allergy
    //
    // Purpose : Routes to the correct antibiotic branch.
    //   No allergy → phenoxymethylpenicillin (abx_penv_select)
    //   Allergy    → q10 (pregnancy check gates eryth vs clari/doxy)
    //
    // Source  : All sinusitis antibiotic PGDs
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q09_penicillin_allergy",
      type: "yes_no",
      text: "Does the patient have a known penicillin allergy?",
      hint: "Includes allergy to flucloxacillin or severe hypersensitivity to any beta-lactam antibiotic. Check National Care Record or patient / carer report.",
      required: true,
      storesTo: "hasPenicillinAllergy",
      yes: "q10_pregnant",
      no: "abx_penv_select",
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q10 — Pregnancy (penicillin-allergic patients only)
    //
    // Purpose : Pregnancy routes to erythromycin (PGD 6f). Both clarithromycin
    //           and doxycycline are excluded in pregnancy.
    //           Auto-answered to false for known-male patients.
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q10_pregnant",
      type: "yes_no",
      text: "Is the patient currently pregnant or suspected to be pregnant?",
      required: true,
      storesTo: "isPregnant",
      autoAnswer: { when: { patientIsFemale: false }, value: false },
      yes: "q10a_pregnant_age_gate",
      no: "q11_myasthenia",
    },
    {
      // Erythromycin PGD (6f) only covers pregnant patients aged ≥16.
      // Pregnant + under 16 has no eligible PGD in this pathway.
      id: "q10a_pregnant_age_gate",
      type: "condition_route",
      conditions: [
        { when: { patientAgeUnder: 16 }, then: { outcomeId: "OUT_CANNOT_PREGNANT_UNDER16" } },
      ],
      default: "abx_eryth_select",
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q11 — Myasthenia gravis
    //
    // Purpose : Myasthenia gravis is a contraindication for BOTH clarithromycin
    //           (PGD 6d) and doxycycline (PGD 6e). Screening it once here —
    //           before the drug-specific screens — avoids asking it twice and
    //           routes directly to cannot-supply when present.
    //
    // Source  : PGD 6d exclusion: "Known myasthenia gravis"
    //           PGD 6e exclusion: "Known myasthenia gravis"
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q11_myasthenia",
      type: "yes_no",
      text: "Does the patient have known myasthenia gravis?",
      hint: "Myasthenia gravis is a contraindication for both clarithromycin and doxycycline. Supply is not possible under this pathway.",
      required: true,
      yes: { outcomeId: "OUT_CANNOT_SUPPLY" },
      no: "q12_doxy_screen",
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q12 — Doxycycline exclusion screen
    //
    // Purpose : Screen for conditions that specifically exclude doxycycline
    //           (PGD 6e). Items shared across all PGDs were already captured
    //           at q02 and are not repeated here.
    //
    //   Any selected → q13a (doxy excluded — check if clari also excluded)
    //   None selected → q13b (doxy viable — check if clari also viable)
    //
    // Source  : PGD 6e (doxy) exclusion criteria — doxy-specific items only
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q12_doxy_screen",
      type: "checklist",
      text: "Do any of the following apply to the patient?",
      hint: "These conditions exclude doxycycline (PGD 6e). If any apply, clarithromycin will be assessed as an alternative.",
      items: [
        {
          id: "doxy_breastfeeding",
          text: "Currently breastfeeding",
          // Doxy PGD 6e excludes breastfeeding; clari PGD 6d permits it with monitoring
        },
        {
          id: "doxy_allergy_tetracycline",
          text: "Known allergy or hypersensitivity to doxycycline or any tetracycline antibiotic",
        },
        {
          id: "doxy_intracranial_htn",
          text: "History of intracranial hypertension (including idiopathic intracranial hypertension)",
        },
        {
          id: "doxy_sle",
          text: "Known systemic lupus erythematosus (SLE)",
        },
        {
          id: "doxy_porphyria",
          text: "Known porphyria",
        },
        {
          id: "doxy_long_term_tetracycline",
          text: "Currently on long-term tetracycline (e.g. doxycycline for acne, tetracycline for malaria prophylaxis)",
        },
        {
          id: "doxy_retinoids",
          text: "Currently taking oral retinoids (e.g. isotretinoin, acitretin)",
          // Risk of raised intracranial pressure when combined with doxycycline
        },
        {
          id: "doxy_enzyme_inducers",
          text: "Taking enzyme-inducing medicines: carbamazepine, fosphenytoin, phenobarbitone, primidone, phenytoin, or rifampicin",
          // These reduce doxycycline plasma levels, potentially below therapeutic range
        },
      ],
      includeNoneOption: true,
      anySelected: "q13a_clari_screen_doxy_out", // doxy excluded → check clari
      noneSelected: "q13b_clari_screen_doxy_ok",  // doxy viable   → check clari
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q13a — Clarithromycin exclusion screen (doxycycline already excluded)
    //
    // Purpose : Doxycycline was excluded at q12. This screen determines
    //           whether clarithromycin is also excluded.
    //
    //   Any selected → both drugs excluded → OUT_CANNOT_SUPPLY
    //   None selected → only doxy excluded → abx_clari_only_select
    //
    // Source  : PGD 6d (clari) exclusion criteria — clari-specific items only
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q13a_clari_screen_doxy_out",
      type: "checklist",
      text: "Do any of the following apply to the patient?",
      hint: "Doxycycline has been excluded. If any of these also apply, no antibiotic can be supplied under this pathway.",
      items: [
        {
          id: "clari_qt_history",
          text: "Known QT prolongation, congenital long QT syndrome, or history of ventricular arrhythmia (including torsades de pointes)",
          linkRefs: ["crediblemeds-qt", "sads-drugs-avoid"],
        },
        {
          id: "clari_qt_drugs",
          text: "Currently taking medicines known to prolong the QT interval",
          linkRefs: ["crediblemeds-qt", "sads-drugs-avoid"],
        },
        {
          id: "clari_electrolytes",
          text: "Uncorrected hypokalaemia (low potassium) or hypomagnesaemia (low magnesium)",
          // Electrolyte disturbances increase risk of QT prolongation with clarithromycin
        },
        {
          id: "clari_heart",
          text: "Known heart disease — coronary artery disease, severe cardiac insufficiency, or resting bradycardia below 50 bpm",
        },
        {
          id: "clari_simvastatin",
          text: "Currently taking simvastatin or lovastatin",
          // Absolute contraindication — clarithromycin inhibits CYP3A4, raising statin levels and rhabdomyolysis risk
        },
        {
          id: "clari_colchicine",
          text: "Currently taking colchicine",
        },
        {
          id: "clari_ticagrelor",
          text: "Currently taking ticagrelor or ranolazine",
        },
        {
          id: "clari_ergot",
          text: "Currently taking ergotamine or dihydroergotamine",
        },
        {
          id: "clari_midazolam",
          text: "Currently taking oral midazolam",
        },
        {
          id: "clari_macrolide",
          text: "Currently on long-term macrolide antibiotic (erythromycin, azithromycin, or clarithromycin)",
        },
        {
          id: "clari_ckd",
          text: "CKD stage 4 or 5 (eGFR below 30 mL/min/1.73m²)",
        },
        {
          id: "clari_liver",
          text: "Known or suspected severe hepatic impairment",
        },
        {
          id: "clari_cyp3a",
          text: "Taking medicines with clinically significant CYP3A4 interactions",
          linkRefs: ["indiana-flockhart"],
        },
        {
          id: "clari_typhoid",
          text: "Oral typhoid vaccine received within the last 3 days, or due within the next 3 days",
        },
      ],
      includeNoneOption: true,
      anySelected: { outcomeId: "OUT_CANNOT_SUPPLY" }, // doxy excluded + clari excluded = no supply
      noneSelected: "abx_clari_only_select",             // doxy excluded, clari clear
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Q13b — Clarithromycin exclusion screen (doxycycline still viable)
    //
    // Purpose : Doxycycline cleared q12. This screen determines whether
    //           clarithromycin is also viable.
    //
    //   Any selected → clari excluded, doxy clear → abx_doxy_only_select
    //   None selected → both drugs viable → abx_pen_allergy_select (pharmacist chooses)
    //
    // Note    : Items are identical to q13a — only routing targets differ,
    //           reflecting whether doxycycline is still available as a fallback.
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "q13b_clari_screen_doxy_ok",
      type: "checklist",
      text: "Do any of the following apply to the patient?",
      hint: "These conditions exclude clarithromycin. If any apply, doxycycline will be offered instead.",
      items: [
        // Identical items to q13a — routing targets differ based on doxy viability
        {
          id: "clari_qt_history",
          text: "Known QT prolongation, congenital long QT syndrome, or history of ventricular arrhythmia (including torsades de pointes)",
          linkRefs: ["crediblemeds-qt", "sads-drugs-avoid"],
        },
        {
          id: "clari_qt_drugs",
          text: "Currently taking medicines known to prolong the QT interval",
          linkRefs: ["crediblemeds-qt", "sads-drugs-avoid"],
        },
        {
          id: "clari_electrolytes",
          text: "Uncorrected hypokalaemia (low potassium) or hypomagnesaemia (low magnesium)",
        },
        {
          id: "clari_heart",
          text: "Known heart disease — coronary artery disease, severe cardiac insufficiency, or resting bradycardia below 50 bpm",
        },
        {
          id: "clari_simvastatin",
          text: "Currently taking simvastatin or lovastatin",
        },
        {
          id: "clari_colchicine",
          text: "Currently taking colchicine",
        },
        {
          id: "clari_ticagrelor",
          text: "Currently taking ticagrelor or ranolazine",
        },
        {
          id: "clari_ergot",
          text: "Currently taking ergotamine or dihydroergotamine",
        },
        {
          id: "clari_midazolam",
          text: "Currently taking oral midazolam",
        },
        {
          id: "clari_macrolide",
          text: "Currently on long-term macrolide antibiotic (erythromycin, azithromycin, or clarithromycin)",
        },
        {
          id: "clari_ckd",
          text: "CKD stage 4 or 5 (eGFR below 30 mL/min/1.73m²)",
        },
        {
          id: "clari_liver",
          text: "Known or suspected severe hepatic impairment",
        },
        {
          id: "clari_cyp3a",
          text: "Taking medicines with clinically significant CYP3A4 interactions",
          linkRefs: ["indiana-flockhart"],
        },
        {
          id: "clari_typhoid",
          text: "Oral typhoid vaccine received within the last 3 days, or due within the next 3 days",
        },
      ],
      includeNoneOption: true,
      anySelected: "abx_doxy_only_select",   // clari excluded, doxy viable
      noneSelected: "abx_pen_allergy_select", // both clear — pharmacist chooses
    },

    // ═══════════════════════════════════════════════════════════════════════
    // DRUG SELECTION NODES
    //
    // Five routes reach drug selection — see flow diagram at top of file.
    // All routes feed into abx_pgd_exclusions for the formal supply check.
    // ═══════════════════════════════════════════════════════════════════════

    {
      // No penicillin allergy — phenoxymethylpenicillin auto-selected (PGD 6c).
      id: "abx_penv_select",
      type: "drug_selection",
      variant: "auto",
      rules: [
        { when: { hasPenicillinAllergy: false }, pgdId: "pgd-6c-penv-sinusitis" },
      ],
      next: "abx_pgd_exclusions",
    },

    {
      // Penicillin allergy + pregnant (≥16) — erythromycin auto-selected (PGD 6f).
      // Clari and doxy both excluded in pregnancy.
      id: "abx_eryth_select",
      type: "drug_selection",
      variant: "auto",
      rules: [
        { when: { hasPenicillinAllergy: true, isPregnant: true }, pgdId: "pgd-6f-eryth-sinusitis" },
      ],
      next: "abx_pgd_exclusions",
    },

    {
      // Doxycycline excluded at q12; clarithromycin cleared at q13a.
      // Clarithromycin auto-selected — no override to doxy offered
      // (doxy was explicitly excluded by pre-screening).
      id: "abx_clari_only_select",
      type: "drug_selection",
      variant: "auto",
      clinicalNote: "Doxycycline excluded during pre-screening — clarithromycin selected automatically.",
      rules: [
        { when: { hasPenicillinAllergy: true, isPregnant: false }, pgdId: "pgd-6d-clari-sinusitis" },
      ],
      next: "abx_pgd_exclusions",
    },

    {
      // Clarithromycin excluded at q13b; doxycycline cleared at q12.
      // Doxycycline auto-selected — no override to clari offered
      // (clari was explicitly excluded by pre-screening).
      id: "abx_doxy_only_select",
      type: "drug_selection",
      variant: "auto",
      clinicalNote: "Clarithromycin excluded during pre-screening — doxycycline selected automatically.",
      rules: [
        { when: { hasPenicillinAllergy: true, isPregnant: false }, pgdId: "pgd-6e-doxy-sinusitis" },
      ],
      next: "abx_pgd_exclusions",
    },

    {
      // Both drugs cleared pre-screening at q12 and q13b.
      // Clarithromycin recommended as default (simpler interaction profile;
      // suitable in breastfeeding; no positional or dietary restrictions).
      // Doxycycline available as a justified override.
      id: "abx_pen_allergy_select",
      type: "drug_selection",
      variant: "equal_choice",
      clinicalNote: "Both clarithromycin and doxycycline have cleared pre-screening. Select the most appropriate antibiotic for this patient.",
      rules: [
        {
          when: { hasPenicillinAllergy: true, isPregnant: false },
          pgdId: "pgd-6d-clari-sinusitis",
        },
        {
          when: { hasPenicillinAllergy: true, isPregnant: false },
          pgdId: "pgd-6e-doxy-sinusitis",
        },
      ],
      next: "abx_pgd_exclusions",
    },

    // ═══════════════════════════════════════════════════════════════════════
    // FORMAL PGD EXCLUSION CHECK
    //
    // Purpose : Runs the full preSupplyChecks list for whichever PGD was
    //           selected. This is the clinical record and must run regardless
    //           of pre-screening — it captures remaining drug-specific items
    //           and formally documents the supply decision.
    //
    // Note    : Some items will overlap with pre-screening nodes. This is
    //           intentional — pre-screening determines which drugs to offer;
    //           the PGD check is the formal supply record.
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "abx_pgd_exclusions",
      type: "pgd_exclusion_check",
      anyTriggered: { outcomeId: "OUT_CANNOT_SUPPLY" },
      allCleared: { outcomeId: "OUT_SUPPLY_ANTIBIOTIC" },
    },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // OUTCOMES
  // ═══════════════════════════════════════════════════════════════════════════
  outcomes: {

    // ── Supply ──────────────────────────────────────────────────────────────

    OUT_SUPPLY_STEROID: {
      id: "OUT_SUPPLY_STEROID",
      type: "outcome",
      outcomeType: "supply",
      title: "Supply intranasal corticosteroid",
      reason: "Acute sinusitis criteria met and selected intranasal corticosteroid PGD exclusions cleared.",
      safetyNetting: [
        "Most sinusitis symptoms improve within 2 to 3 weeks",
        "Use simple analgesia such as paracetamol or ibuprofen if suitable",
        "Seek urgent help if swelling around the eye, vision changes, severe headache, neck stiffness, confusion, or rapid worsening occurs",
        "Return to pharmacy or contact GP if symptoms worsen or do not improve as expected",
      ],

      linkRefs: ["nhs-sinusitis", "target-rti-leaflet"],
    },

    OUT_SUPPLY_ANTIBIOTIC: {
      id: "OUT_SUPPLY_ANTIBIOTIC",
      type: "outcome",
      outcomeType: "supply",
      title: "Supply antibiotic",
      reason: "Antibiotic treatment indicated by the pathway and selected PGD exclusions cleared.",
      safetyNetting: [
        "Symptoms should start to improve within 2 to 3 days of starting antibiotics",
        "Complete the full course even if symptoms improve earlier",
        "Seek urgent help if swelling around the eye, vision changes, severe headache, neck stiffness, confusion, or rapid worsening occurs",
        "Return to pharmacy or contact GP if symptoms do not improve after completing the course",
      ],

      linkRefs: ["nhs-sinusitis", "target-rti-leaflet"],
    },

    // ── Self-care ────────────────────────────────────────────────────────────

    OUT_SELF_CARE_DIAGNOSIS_UNLIKELY: {
      id: "OUT_SELF_CARE_DIAGNOSIS_UNLIKELY",
      type: "outcome",
      outcomeType: "self_care",
      title: "Sinusitis diagnosis not confirmed — self-care",
      reason: "Diagnostic criteria at q03 were not met. Provide self-care advice and consider alternative diagnoses.",
      safetyNetting: [
        "Use simple analgesia such as paracetamol or ibuprofen if suitable",
        "Consider saline nasal irrigation for symptomatic relief",
        "Return if symptoms persist, worsen, or new red flag symptoms develop",
      ],

      linkRefs: ["nhs-sinusitis"],
    },

    OUT_SELF_CARE_DURATION: {
      id: "OUT_SELF_CARE_DURATION",
      type: "outcome",
      outcomeType: "self_care",
      title: "Self-care — symptoms present for 10 days or fewer",
      reason: "Symptoms have been present for 10 days or fewer. Sinusitis is usually self-limiting at this stage and supply is not appropriate.",
      safetyNetting: [
        "Acute sinusitis usually resolves on its own within 2 to 3 weeks",
        "Use simple analgesia such as paracetamol or ibuprofen if suitable",
        "Saline nasal irrigation may help relieve congestion",
        "Return to pharmacy or contact GP if symptoms worsen, persist beyond 10 days with no improvement, or red flag symptoms develop",
      ],

      linkRefs: ["nhs-sinusitis"],
    },

    OUT_SELF_CARE_NO_BACTERIAL: {
      id: "OUT_SELF_CARE_NO_BACTERIAL",
      type: "outcome",
      outcomeType: "self_care",
      title: "Self-care — fewer than 2 bacterial features, corticosteroid declined",
      reason: "Fewer than 2 bacterial features were present — antibiotics are not indicated. Intranasal corticosteroid was offered but declined.",
      safetyNetting: [
        "Acute sinusitis is usually caused by a virus and resolves without treatment in 2 to 3 weeks",
        "Use simple analgesia such as paracetamol or ibuprofen if suitable",
        "Saline nasal irrigation may help relieve congestion",
        "Return to pharmacy or contact GP if symptoms worsen, persist beyond 3 weeks, or you develop severe headache, visual changes, facial swelling, or high fever",
      ],

      linkRefs: ["nhs-sinusitis"],
    },

    // ── Cannot supply ────────────────────────────────────────────────────────

    OUT_CANNOT_UNDER12: {
      id: "OUT_CANNOT_UNDER12",
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — patient under 12 years",
      reason: "This pathway covers adults and children aged 12 years and over only.",
      safetyNetting: ["Refer to GP or appropriate provider for assessment"],

    },

    OUT_CANNOT_PREGNANT_UNDER16: {
      id: "OUT_CANNOT_PREGNANT_UNDER16",
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — pregnant and under 16",
      reason: "No sinusitis PGD covers pregnancy in patients under 16 years of age.",
      safetyNetting: ["Refer to GP or appropriate provider for assessment"],

    },

    OUT_CANNOT_SUPPLY: {
      id: "OUT_CANNOT_SUPPLY",
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — exclusion criteria met",
      reason: "One or more exclusion criteria prevent supply under this pathway.",
      safetyNetting: [
        "Refer to GP or appropriate prescriber if antibiotic treatment remains clinically indicated",
        "Document the specific exclusion(s) that prevented supply in the clinical record",
        "Provide self-care advice and TARGET RTI leaflet",
      ],

      linkRefs: ["nhs-sinusitis", "target-rti-leaflet"],
    },
  },
};
